import React from 'react';

import { Inject } from '@odinjs/odin';
import { Link, ThemeService, ThemeTonalRange, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnCatch, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { PromiseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpError } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { LoginAPI } from '@philips-internal/oswald-ui-foundation';
import { isMobile } from '@philips-internal/oswald-ui-utils';

import { FragmentNames, LoginAliases } from '../codegen';

@Controller({ fragment: FragmentNames.LOGIN })
export default class LoginController {

  @Inject(LoginAliases.MAIN_LOGIN)
  loginAPI!: LoginAPI;

  @OnMount(LoginAliases.MAIN_LOGIN)
  onMountLogin(): void {
    const messageExpression = MessageService.translate('tws.about-box.message').replace('#@CURRENT_YEAR#@', new Date().getFullYear().toString());
    this.loginAPI.getAboutBoxProps().messageExpression = messageExpression;
    ThemeService.setContentTonalRange(ThemeTonalRange.BRIGHT);
    const isHD = window.screen.availWidth > 768;
    ToastService.closeAll();
    ToastService.open({
      action: (<Link value={MessageService.translate('login.cookies-policy.btn.cookies.policy')} route="/login/cookies-policy"/>),
      dismissExpression: MessageService.translate('message.ok'),
      dismissible: true,
      highlight: true,
      id: 'login-controller.cookies-policy',
      level: ToastLevel.WARNING,
      message: MessageService.translate('login.cookies-policy.msg.cookies.policy') + ' ' +
        MessageService.translate(!isMobile() ? 'login.cookies-policy.msg.cookies.complement' : 'login.cookies-policy.msg.cookies.complement-mobile'),
      offset: 0,
      placement: Placement.BOTTOM,
      showIcon: true,
      type: isHD ? ToastType.BANNER : ToastType.BUBBLE,
    });
  }

  @OnUnmount({ target: LoginAliases.MAIN_LOGIN })
  onUnmount(): void {
    ToastService.close('login-controller.cookies-policy');
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
  }

  @OnCatch({ target: LoginAliases.MAIN_LOGIN })
  onCatch(event: PromiseEvent<HttpError>): void {
    const { response } = event.response;
    if (!response) {
      return;
    }
    let message = '';
    switch (response.status) {
      case 400:
        void response.json().then(resp => {
          if (resp) {
            const { expressionKey } = resp;
            this.showNotification(expressionKey);
          }
        });
        break;
      case 401:
        message = 'login.unauthorized';
        break;
      case 403:
        message = 'login.error.not-found';
        break;
      case 422:
        message = 'login.error.empty';
        break;
    }
    this.showNotification(message);
  }

  showNotification(message: string): void {
    this.loginAPI.setNotification({
      level: ToastLevel.WARNING,
      highlight: true,
      message: MessageService.translate(message),
    });
  }
}
